import React, { ReactNode } from "react";
import styled from "styled-components";

/**
 * SOREMO 共通レスポンスチップ（旧 `preview/components/ClientTagChips` 内
 * ローカル実装のグローバル化）。
 *
 * 目的: **チェックバックでの補足コミュニケーション**（いいね! 等）。
 * 検収や購入のような直接意思決定を伴わない範囲に限定する。
 *  - プレビューページの候補でのクライアントレスポンス（OK / 検討 /
 *    近いが.. / 違う）
 *
 * タグ系コンポーネントの使い分けは `FilterChip` のコメントを参照。
 *
 * 視覚仕様:
 *  - pill 型（radius 999px）・11px
 *  - クリックでアクティブ（`active=true`: 青地 + 白文字）
 *  - `onClick` なしは読み取り専用バッジ（background 地 + gray 文字）
 *
 * 排他選択（複数候補から単独選択）は親側で制御する制御コンポーネント。
 * 将来の複数トグル（いいね! 等）にもそのまま対応できる。
 */
export interface ResponseChipProps {
  children: ReactNode;
  /** 選択中（青地 + 白文字）。 */
  active?: boolean;
  /**
   * クリックハンドラ。省略すると読み取り専用バッジ表示になる。
   * イベントを受けるので、カード内配置では `stopPropagation` できる。
   */
  // eslint-disable-next-line no-unused-vars
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
}

const InteractiveChip = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${(p) => (p.$active ? "var(--soremo-blue)" : "var(--soremo-border)")};
  border-radius: 999px;
  background: ${(p) => (p.$active ? "var(--soremo-blue)" : "transparent")};
  color: ${(p) => (p.$active ? "#fff" : "var(--soremo-light-gray)")};
  padding: 3px 10px;
  font-size: 11px;
  line-height: 1.4;
  white-space: nowrap;
  cursor: pointer;
  appearance: none;
  margin: 0;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: var(--soremo-blue);
    color: ${(p) => (p.$active ? "#fff" : "var(--soremo-blue)")};
  }

  &:focus-visible {
    outline: 2px solid var(--soremo-blue);
    outline-offset: 2px;
  }
`;

/** 読み取り専用バッジ */
const ReadonlyChip = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: var(--soremo-background);
  color: var(--soremo-gray);
  border: 1px solid var(--soremo-border);
  padding: 3px 10px;
  font-size: 11px;
  line-height: 1.4;
  white-space: nowrap;
`;

const ResponseChip: React.FC<ResponseChipProps> = ({
  children,
  active = false,
  onClick,
  className,
}) => {
  if (!onClick) {
    return <ReadonlyChip className={className}>{children}</ReadonlyChip>;
  }
  return (
    <InteractiveChip
      type="button"
      $active={active}
      onClick={onClick}
      aria-pressed={active}
      className={className}
    >
      {children}
    </InteractiveChip>
  );
};

export default ResponseChip;
